import type React from 'react'

import {
	BodyL,
	BodyLStrong,
	BodyS,
	BORDER_RADIUS,
	COLORS,
	DisclosureCard,
	formatDate,
	formatNumberAsCurrency,
	SPACING,
} from '@monthio/design-system'
import { styled } from 'styled-components'

export interface IncomeAssessmentTransactionDto {
	amount: number
	date: string
	text: string
}

export const IncomeVerificationContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${SPACING.l};
`

interface TransactionRecordProps {
	children?: never
	currency: string
	locale: string
	transaction: IncomeAssessmentTransactionDto
}

export const TransactionWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	padding: ${SPACING.s} ${SPACING.m};
	gap: ${SPACING.m};
	align-items: center;
`

export const TransactionTitle = styled.div`
	display: flex;
	flex-direction: column;
`

const DateText = styled(BodyS)`
	color: ${COLORS.graphics.foreground.text.sub};
`

export const TransactionRecord: React.FC<TransactionRecordProps> = ({
	currency,
	locale,
	transaction,
}) => {
	const date = formatDate(transaction.date, {
		format: 'short',
		showYear: true,
	})

	return (
		<TransactionWrapper>
			<TransactionTitle>
				<BodyL>{transaction.text}</BodyL>
				<DateText>{date}</DateText>
			</TransactionTitle>
			<BodyLStrong>
				{formatNumberAsCurrency(transaction.amount, locale, {
					currency,
				})}
			</BodyLStrong>
		</TransactionWrapper>
	)
}

interface TransactionRecordListCardProps {
	chainId: number
	currency: string
	locale: string
	title: string
	transactions: IncomeAssessmentTransactionDto[]
}

const TransactionRecordListCardWrapper = styled.div`
	border-top: 1px solid ${COLORS.graphics.stroke.secondary};
`

export const TransactionRecordWrapper = styled.div`
	display: flex;
	flex-direction: column;
`

export const TransactionRecordListCard = ({
	chainId,
	currency,
	locale,
	title,
	transactions,
}: TransactionRecordListCardProps): JSX.Element => {
	const sortedTransactions = transactions.sort(
		(x, y) => -1 * x.date.localeCompare(y.date),
	)
	return (
		<TransactionRecordListCardWrapper>
			<DisclosureCard title={title} headingLevel={3} size="s" isInContainer>
				<TransactionRecordWrapper>
					{sortedTransactions.map((transaction) => (
						<TransactionRecord
							currency={currency}
							key={`chain_${chainId}_transaction_record_${transaction.text}_${transaction.date}_${transaction.amount}`}
							locale={locale}
							transaction={transaction}
						/>
					))}
				</TransactionRecordWrapper>
			</DisclosureCard>
		</TransactionRecordListCardWrapper>
	)
}

export const ChainWrapper = styled.div`
	border-radius: ${BORDER_RADIUS.s};
	background-color: ${COLORS.graphics.surface.cardPrimary};
`

export const ChainInputWrapper = styled.div`
	padding: ${SPACING.m};
	display: flex;
	flex-direction: column;
	gap: ${SPACING.m};
`
